import { useState } from "react";
import {
  Search,
  SlidersHorizontal,
  RefreshCw,
  Store,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
export const T = {
  brand:      "#2563EB",
  brandDark:  "#1D4ED8",
  brandSoft:  "#EFF6FF",
  brandBorder:"#BFDBFE",
  success:    "#10B981",
  successSoft:"#ECFDF5",
  danger:     "#EF4444",
  dangerSoft: "#FEF2F2",
  warning:    "#F59E0B",
  warningSoft:"#FFFBEB",
  slate800:   "#1E293B",
  slate600:   "#475569",
  slate400:   "#94A3B8",
  slate100:   "#F1F5F9",
  slate50:    "#F8FAFC",
};

// ─── ATOM: StatusBadge ───────────────────────────────────────────────────────
export function StatusBadge({ active }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
      style={{
        background: active ? T.successSoft : T.dangerSoft,
        color:      active ? T.success     : T.danger,
        border:     `1px solid ${active ? "#A7F3D0" : "#FECACA"}`,
      }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: active ? T.success : T.danger }} />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

// ─── ATOM: VerifiedBadge ─────────────────────────────────────────────────────
export function VerifiedBadge({ verified }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
      style={{
        background: verified ? T.brandSoft : T.slate50,
        color:      verified ? T.brand     : T.slate400,
        border:     `1px solid ${verified ? T.brandBorder : "#E2E8F0"}`,
      }}
    >
      {verified ? "Onboarded" : "Pending"}
    </span>
  );
}

// ─── ATOM: UsageStatusBadge ──────────────────────────────────────────────────
export function UsageStatusBadge({ flag }) {
  const map = {
    working:            { label: "Using",       bg: T.successSoft, color: T.success,  border: "#A7F3D0" },
    inactive:           { label: "Not Using",   bg: T.warningSoft, color: T.warning,  border: "#FDE68A" },
    activation_pending: { label: "ACT-PD",      bg: T.brandSoft,   color: T.brand,    border: T.brandBorder },
    // Also handle already-mapped strings
    Using:              { label: "Using",       bg: T.successSoft, color: T.success,  border: "#A7F3D0" },
    "Not Using":        { label: "Not Using",   bg: T.warningSoft, color: T.warning,  border: "#FDE68A" },
    "ACT-PD":           { label: "ACT-PD", bg: T.brandSoft,   color: T.brand,    border: T.brandBorder },
  };
  const { label = flag || "Unknown", bg = T.slate100, color = T.slate600, border = "#CBD5E1" } =
    map[flag] || {};
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
      style={{ background: bg, color, border: `1px solid ${border}` }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

// ─── ATOM: CreatedByBadge ────────────────────────────────────────────────────
export function CreatedByBadge({ createdById, agentId, createdByName, authUserId }) {
  const isSelf  = createdById === authUserId;
  const isAgent = !isSelf && agentId !== null;
  const label   = isSelf ? "Self" : isAgent ? "By Agent" : "Other";
  const color   = isSelf ? T.success : isAgent ? T.brand : T.slate400;
  const bg      = isSelf ? T.successSoft : isAgent ? T.brandSoft : T.slate50;
  const border  = isSelf ? "#A7F3D0" : isAgent ? T.brandBorder : "#E2E8F0";
  return (
    <div>
      <span
        className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
        style={{ background: bg, color, border: `1px solid ${border}` }}
      >
        {label}
      </span>
      {createdByName && (
        <p className="text-[10px] text-slate-400 mt-0.5">{createdByName}</p>
      )}
    </div>
  );
}

// ─── ATOM: Avatar ─────────────────────────────────────────────────────────────
export function Avatar({ name, size = 10 }) {
  const initials = (name || "?")
    .split(" ")
    .map(w => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className={`w-${size} h-${size} rounded-xl flex items-center justify-center font-black text-white text-[11px] shrink-0`}
      style={{ background: `linear-gradient(135deg,${T.brand},${T.brandDark})` }}
    >
      {initials}
    </div>
  );
}

// ─── MOLECULE: EmptyState ────────────────────────────────────────────────────
function EmptyState({ label }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
        style={{ background: T.brandSoft }}
      >
        <Store size={24} style={{ color: T.brand, opacity: 0.5 }} />
      </div>
      <p className="text-[14px] font-bold text-slate-700">No {label} found</p>
      <p className="text-[12px] text-slate-400 mt-1">
        Try changing your search or filter criteria
      </p>
    </div>
  );
}

// ─── MOLECULE: Loader ─────────────────────────────────────────────────────────
function InlineLoader() {
  return (
    <div className="flex items-center justify-center py-16">
      <div
        className="w-8 h-8 rounded-full border-[3px] border-blue-100 border-t-blue-500 animate-spin"
      />
    </div>
  );
}

// ─── MOLECULE: Pagination ────────────────────────────────────────────────────
function TablePagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  const delta = 1;
  const left  = Math.max(2, currentPage - delta);
  const right = Math.min(totalPages - 1, currentPage + delta);

  pages.push(1);
  if (left > 2) pages.push("...");
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < totalPages - 1) pages.push("...");
  if (totalPages > 1) pages.push(totalPages);

  const btnBase =
    "w-8 h-8 rounded-lg text-[12px] font-bold flex items-center justify-center transition-all";

  return (
    <div className="mt-5 flex items-center justify-center gap-1">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`${btnBase} border border-slate-200 text-slate-500 hover:border-blue-300 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed`}
      >
        <ChevronLeft size={14} />
      </button>

      {pages.map((p, i) =>
        p === "..." ? (
          <span key={`ellipsis-${i}`} className="w-8 text-center text-[12px] text-slate-400">
            …
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={btnBase}
            style={
              p === currentPage
                ? {
                    background: `linear-gradient(135deg,${T.brand},${T.brandDark})`,
                    color: "#fff",
                    boxShadow: `0 3px 10px ${T.brand}44`,
                  }
                : { border: "1px solid #E2E8F0", color: T.slate600 }
            }
          >
            {p}
          </button>
        )
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`${btnBase} border border-slate-200 text-slate-500 hover:border-blue-300 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed`}
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN: StyledTable
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Props:
 *  title             string
 *  icon              LucideIcon component (optional, defaults to Store)
 *  total             number  (shown below title)
 *  loading           boolean
 *  emptyLabel        string  (used in "No {emptyLabel} found")
 *
 *  headers           string[]  — desktop table column headers
 *  items             any[]
 *
 *  renderRow(item, index)  → <tr> element   (desktop)
 *  renderCard(item)        → JSX element    (mobile)
 *
 *  searchValue       string
 *  onSearchChange    (val: string) => void
 *  searchField       string          (optional — only if searchOptions provided)
 *  searchOptions     [{ value, label }]  (optional)
 *  onSearchFieldChange (val) => void  (optional)
 *  searchPlaceholder string  (optional)
 *
 *  onRefresh         () => void
 *
 *  currentPage       number
 *  totalPages        number
 *  onPageChange      (page: number) => void
 *
 *  headerRight       ReactNode  (extra controls rendered beside search/refresh)
 */
export default function StyledTable({
  title = "Directory",
  icon: Icon = Store,
  total,
  loading = false,
  emptyLabel = "records",
  headers = [],
  items = [],
  renderRow,
  renderCard,
  searchValue = "",
  onSearchChange,
  searchField,
  searchOptions,
  onSearchFieldChange,
  searchPlaceholder,
  onRefresh,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  headerRight,

  usageFilter,
  onUsageFilterChange,
}) {
  const placeholder =
    searchPlaceholder ||
    (searchField ? `Search by ${searchField.replace(/_/g, " ")}…` : "Search…");

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden"
      style={{
        border:     "1px solid #E2E8F0",
        boxShadow:  "0 2px 16px rgba(0,0,0,0.06)",
        fontFamily: "'Plus Jakarta Sans','DM Sans',sans-serif",
      }}
    >
      {/* ── HEADER ── */}
      <div
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b"
        style={{ borderColor: "#F1F5F9" }}
      >
        {/* Title */}
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: T.brandSoft }}
          >
            <Icon size={15} style={{ color: T.brand }} />
          </div>
          <div>
            <h2 className="text-[14px] font-black text-slate-800">{title}</h2>
            {total != null && total > 0 && (
              <p className="text-[11px] text-slate-400 font-medium">{total} records</p>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 flex-wrap justify-end">

            {/* Usage Filter */}
            {onUsageFilterChange && (
            <div className="flex items-center gap-2 flex-wrap">
                {[
                { label: "All", value: "" },
                { label: "Using", value: "Using" },
                { label: "Not Using", value: "Not Using" },
                { label: "ACT-PD", value: "ACT-PD" },
                ].map((item) => (
                <button
                    key={item.label}
                    onClick={() => onUsageFilterChange(item.value)}
                    className="px-3 py-2 rounded-xl text-[12px] font-black transition-all"
                    style={{
                    background:
                        usageFilter === item.value
                        ? T.brand
                        : "#fff",

                    color:
                        usageFilter === item.value
                        ? "#fff"
                        : T.slate600,

                    border: `1px solid ${
                        usageFilter === item.value
                        ? T.brand
                        : "#E2E8F0"
                    }`,
                    }}
                >
                    {item.label}
                </button>
                ))}
            </div>
            )}
            
          {/* Field selector */}
          {searchOptions && onSearchFieldChange && (
            <div className="relative">
              <SlidersHorizontal
                size={12}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: T.slate400 }}
              />
              <select
                value={searchField}
                onChange={e => onSearchFieldChange(e.target.value)}
                className="text-[12px] font-semibold pl-8 pr-3 py-2 rounded-xl border border-slate-200 bg-white outline-none focus:border-blue-400 appearance-none cursor-pointer"
                style={{ color: T.slate600 }}
              >
                {searchOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Search input */}
          {onSearchChange && (
            <div className="relative">
              <Search
                size={13}
                className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: T.slate400 }}
              />
              <input
                type="text"
                placeholder={placeholder}
                value={searchValue}
                onChange={e => onSearchChange(e.target.value)}
                className="text-[12px] font-semibold pl-9 pr-3 py-2 rounded-xl border border-slate-200 outline-none w-44 sm:w-52 transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                style={{ color: T.slate800 }}
              />
            </div>
          )}

          {/* Refresh */}
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="w-9 h-9 rounded-xl flex items-center justify-center border border-slate-200 hover:bg-slate-50 hover:border-blue-300 transition-all"
              title="Refresh"
            >
              <RefreshCw size={14} style={{ color: T.slate400 }} />
            </button>
          )}

          

          {/* Extra slot */}
          {headerRight}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="p-4">
        {loading ? (
          <InlineLoader />
        ) : items.length === 0 ? (
          <EmptyState label={emptyLabel} />
        ) : (
          <>
            {/* Mobile cards */}
            {renderCard && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:hidden">
                {items.map((item, i) => renderCard(item, i))}
              </div>
            )}

            {/* Desktop table */}
            {renderRow && (
              <div
                className={`${renderCard ? "hidden lg:block" : "block"} overflow-x-auto rounded-xl`}
                style={{ border: "1px solid #F1F5F9" }}
              >
                <table className="min-w-full" style={{ borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: T.slate50, borderBottom: "1px solid #E2E8F0" }}>
                      {headers.map((h, i) => (
                        <th
                          key={h + i}
                          className="py-3 px-4 text-left"
                          style={{
                            fontSize:      10,
                            fontWeight:    900,
                            textTransform: "uppercase",
                            letterSpacing: "0.1em",
                            color:         T.slate400,
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, i) => renderRow(item, i))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          </>
        )}
      </div>
    </div>
  );
}